/**
 * Barra de progreso y de proporción.
 *
 * El ancho **no va como estilo inline**: el check de CI lo prohíbe y la regla del
 * proyecto es que todo valor visual salga de un token (`CLAUDE.md` §10.3). Y un
 * `w-[${n}%]` armado con template literal tampoco sirve — Tailwind escanea el
 * código fuente y esa clase no existe hasta que corre.
 *
 * La salida es un mapa de veintiún clases literales, en pasos de 5%. Es más
 * verboso que un `style` y tiene una ventaja real además de cumplir la regla:
 * una barra de progreso con precisión de un píxel no comunica nada distinto que
 * una redondeada al 5%, y el redondeo evita sugerir una exactitud que el dato de
 * abajo no tiene.
 */

const ANCHOS = [
  'w-0',
  'w-[5%]',
  'w-[10%]',
  'w-[15%]',
  'w-[20%]',
  'w-[25%]',
  'w-[30%]',
  'w-[35%]',
  'w-[40%]',
  'w-[45%]',
  'w-[50%]',
  'w-[55%]',
  'w-[60%]',
  'w-[65%]',
  'w-[70%]',
  'w-[75%]',
  'w-[80%]',
  'w-[85%]',
  'w-[90%]',
  'w-[95%]',
  'w-full',
] as const;

/** El ancho redondeado al 5% más cercano. Exportado para los tests. */
export function claseDeAncho(porcentaje: number): string {
  const acotado = Math.min(100, Math.max(0, porcentaje));
  return ANCHOS[Math.round(acotado / 5)] ?? 'w-0';
}

const ALTOS = [
  'h-0',
  'h-[5%]',
  'h-[10%]',
  'h-[15%]',
  'h-[20%]',
  'h-[25%]',
  'h-[30%]',
  'h-[35%]',
  'h-[40%]',
  'h-[45%]',
  'h-[50%]',
  'h-[55%]',
  'h-[60%]',
  'h-[65%]',
  'h-[70%]',
  'h-[75%]',
  'h-[80%]',
  'h-[85%]',
  'h-[90%]',
  'h-[95%]',
  'h-full',
] as const;

/** El alto redondeado al 5%, para las barras verticales (el termómetro). */
export function claseDeAlto(porcentaje: number): string {
  const acotado = Math.min(100, Math.max(0, porcentaje));
  return ALTOS[Math.round(acotado / 5)] ?? 'h-0';
}

export function Barra({
  porcentaje,
  etiqueta,
  color = 'bg-accent',
}: {
  porcentaje: number;
  etiqueta: string;
  color?: string;
}) {
  const redondeado = Math.round(Math.min(100, Math.max(0, porcentaje)));

  return (
    <div
      aria-label={etiqueta}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={redondeado}
      className="h-3 w-full overflow-hidden rounded-full bg-track"
      role="progressbar"
    >
      <span className={`block h-full rounded-full ${color} ${claseDeAncho(redondeado)}`} />
    </div>
  );
}
